import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Edit2, Trash2, Download, Calendar, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const FeriadosPage = () => {
  const [feriados, setFeriados] = useState([]);
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    data: '',
    tipo: 'nacional',
    uf: '',
    cidade: ''
  });

  useEffect(() => {
    loadFeriados();
  }, [anoSelecionado]);

  const loadFeriados = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/feriados?ano=${anoSelecionado}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeriados(response.data);
    } catch (error) {
      console.error('Erro ao carregar feriados:', error);
      toast.error('Erro ao carregar feriados');
    } finally {
      setLoading(false);
    }
  };

  const handleImportarFeriados = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/feriados/importar-brasil/${anoSelecionado}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success(response.data.message);
      loadFeriados();
    } catch (error) {
      console.error('Erro ao importar:', error);
      toast.error('Erro ao importar feriados nacionais');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.data) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        uf: formData.tipo === 'estadual' ? formData.uf : null,
        cidade: formData.tipo === 'municipal' ? formData.cidade : null
      };

      if (editingId) {
        await axios.put(`${API}/api/feriados/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Feriado atualizado!');
      } else {
        await axios.post(`${API}/api/feriados`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Feriado criado!');
      }

      resetForm();
      loadFeriados();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar feriado');
    }
  };

  const handleEdit = (feriado) => {
    setEditingId(feriado.id);
    setFormData({
      nome: feriado.nome,
      data: feriado.data,
      tipo: feriado.tipo,
      uf: feriado.uf || '',
      cidade: feriado.cidade || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente deletar este feriado?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/feriados/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Feriado deletado!');
      loadFeriados();
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast.error('Erro ao deletar feriado');
    }
  };

  const resetForm = () => {
    setFormData({ nome: '', data: '', tipo: 'nacional', uf: '', cidade: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const formatarData = (dataStr) => {
    const [ano, mes, dia] = dataStr.split('-');
    return `${dia}/${mes}/${ano}`;
  };

  const getTipoBadgeColor = (tipo) => {
    const colors = {
      nacional: 'bg-blue-100 text-blue-800',
      estadual: 'bg-green-100 text-green-800',
      municipal: 'bg-purple-100 text-purple-800',
      empresa: 'bg-orange-100 text-orange-800'
    };
    return colors[tipo] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Carregando feriados...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="bg-gradient-to-r from-blue-500 to-purple-500 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar size={32} />
              <div>
                <CardTitle className="text-2xl font-bold">Gestão de Feriados</CardTitle>
                <CardDescription className="text-white/90">
                  Configure feriados nacionais, estaduais e municipais
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-6">
          {/* Controles Superiores */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Label htmlFor="ano">Ano:</Label>
              <Select value={String(anoSelecionado)} onValueChange={(v) => setAnoSelecionado(Number(v))}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027, 2028].map(ano => (
                    <SelectItem key={ano} value={String(ano)}>{ano}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleImportarFeriados}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            >
              <Download size={16} />
              Importar Feriados Nacionais
            </Button>

            <Button
              onClick={() => setShowForm(!showForm)}
              className="flex items-center gap-2 sm:ml-auto"
            >
              <Plus size={16} />
              {showForm ? 'Cancelar' : 'Novo Feriado'}
            </Button>
          </div>

          {/* Alerta Informativo */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="text-sm text-blue-800">
              <strong>Dica:</strong> Use o botão "Importar Feriados Nacionais" para carregar automaticamente 
              os feriados oficiais do Brasil. Depois, você pode adicionar feriados estaduais, municipais ou da empresa.
            </div>
          </div>

          {/* Formulário */}
          {showForm && (
            <Card className="mb-6 border-2 border-blue-200">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nome">Nome do Feriado *</Label>
                      <Input
                        id="nome"
                        value={formData.nome}
                        onChange={(e) => setFormData({...formData, nome: e.target.value})}
                        placeholder="Ex: Dia do Padroeiro"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="data">Data *</Label>
                      <Input
                        id="data"
                        type="date"
                        value={formData.data}
                        onChange={(e) => setFormData({...formData, data: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="tipo">Tipo *</Label>
                      <Select value={formData.tipo} onValueChange={(v) => setFormData({...formData, tipo: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nacional">Nacional</SelectItem>
                          <SelectItem value="estadual">Estadual</SelectItem>
                          <SelectItem value="municipal">Municipal</SelectItem>
                          <SelectItem value="empresa">Empresa</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {formData.tipo === 'estadual' && (
                      <div>
                        <Label htmlFor="uf">UF *</Label>
                        <Input
                          id="uf"
                          value={formData.uf}
                          onChange={(e) => setFormData({...formData, uf: e.target.value.toUpperCase()})}
                          placeholder="Ex: SP"
                          maxLength={2}
                          required
                        />
                      </div>
                    )}

                    {formData.tipo === 'municipal' && (
                      <div>
                        <Label htmlFor="cidade">Cidade *</Label>
                        <Input
                          id="cidade"
                          value={formData.cidade}
                          onChange={(e) => setFormData({...formData, cidade: e.target.value})}
                          placeholder="Ex: São Paulo"
                          required
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button type="submit">
                      {editingId ? 'Atualizar' : 'Salvar'}
                    </Button>
                    <Button type="button" variant="outline" onClick={resetForm}>
                      Cancelar
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Lista de Feriados */}
          <Card>
            <CardHeader>
              <CardTitle>Feriados de {anoSelecionado}</CardTitle>
              <CardDescription>{feriados.length} feriado(s) cadastrado(s)</CardDescription>
            </CardHeader>
            <CardContent>
              {feriados.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  Nenhum feriado cadastrado para {anoSelecionado}
                </div>
              ) : (
                <div className="space-y-2">
                  {feriados.map((feriado) => (
                    <div
                      key={feriado.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-bold text-gray-700">
                            {formatarData(feriado.data)}
                          </span>
                          <h3 className="font-semibold">{feriado.nome}</h3>
                          <span className={`text-xs px-2 py-1 rounded-full ${getTipoBadgeColor(feriado.tipo)}`}>
                            {feriado.tipo.charAt(0).toUpperCase() + feriado.tipo.slice(1)}
                          </span>
                          {feriado.uf && (
                            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                              {feriado.uf}
                            </span>
                          )}
                          {feriado.cidade && (
                            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                              {feriado.cidade}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(feriado)}
                        >
                          <Edit2 size={14} />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(feriado.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </div>
  );
};

export default FeriadosPage;
